// SHA-256 and HMAC-SHA256 (FIPS 180-4, RFC 2104), in about a hundred lines of arithmetic.
//
// Why not `node:crypto`? Because this package runs in four places and only one of them is Node
// (spec/03 §4): the kiosk is a WebView, the apps are Hermes, and `node:crypto` is banned here by
// the purity rule anyway. Web Crypto would do the job in three of the four, but it is asynchronous
// — and `verifyOfflineCode` is called by a station deciding whether to unlock a PC, where a
// promise is a worse answer than 42 hashes of arithmetic. There is no dependency to add either: a
// hash function is a fixed, testable, 100-line object, and this one is checked against the RFC
// 4231 vectors in `test-vectors/hmac-sha256.json`, which the C# side runs too.
//
// Nothing here is a secret-generating primitive: there is no randomness in this package (see the
// `pure` lint preset). Secrets are minted where they are stored.
const ROUND_CONSTANTS = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98,
    0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8,
    0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819,
    0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
    0xc67178f2,
]);
const INITIAL_STATE = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);
export const SHA256_BLOCK_BYTES = 64;
export const SHA256_DIGEST_BYTES = 32;
/** The SHA-256 digest of a byte string: 32 bytes. */
export function sha256(message) {
    // Padding: the message, a 0x80 byte, zeroes, and the bit length as 64 bits big-endian.
    const paddedLength = Math.ceil((message.length + 9) / SHA256_BLOCK_BYTES) * SHA256_BLOCK_BYTES;
    const padded = new Uint8Array(paddedLength);
    padded.set(message);
    padded[message.length] = 0x80;
    const view = new DataView(padded.buffer);
    // The length in bits as two 32-bit halves, so no BigInt is needed on a hot path.
    view.setUint32(paddedLength - 8, Math.floor(message.length / 0x2000_0000), false);
    view.setUint32(paddedLength - 4, (message.length * 8) >>> 0, false);
    const state = new Uint32Array(INITIAL_STATE);
    const schedule = new Uint32Array(64);
    for (let block = 0; block < paddedLength; block += SHA256_BLOCK_BYTES) {
        for (let index = 0; index < 16; index += 1) {
            schedule[index] = view.getUint32(block + index * 4, false);
        }
        for (let index = 16; index < 64; index += 1) {
            const w15 = at(schedule, index - 15);
            const w2 = at(schedule, index - 2);
            const s0 = (rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3)) >>> 0;
            const s1 = (rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10)) >>> 0;
            schedule[index] = (at(schedule, index - 16) + s0 + at(schedule, index - 7) + s1) >>> 0;
        }
        let a = at(state, 0);
        let b = at(state, 1);
        let c = at(state, 2);
        let d = at(state, 3);
        let e = at(state, 4);
        let f = at(state, 5);
        let g = at(state, 6);
        let h = at(state, 7);
        for (let index = 0; index < 64; index += 1) {
            const sigma1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
            const choose = ((e & f) ^ (~e & g)) >>> 0;
            const temp1 = (h + sigma1 + choose + at(ROUND_CONSTANTS, index) + at(schedule, index)) >>> 0;
            const sigma0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
            const majority = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
            const temp2 = (sigma0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }
        state[0] = (at(state, 0) + a) >>> 0;
        state[1] = (at(state, 1) + b) >>> 0;
        state[2] = (at(state, 2) + c) >>> 0;
        state[3] = (at(state, 3) + d) >>> 0;
        state[4] = (at(state, 4) + e) >>> 0;
        state[5] = (at(state, 5) + f) >>> 0;
        state[6] = (at(state, 6) + g) >>> 0;
        state[7] = (at(state, 7) + h) >>> 0;
    }
    const digest = new Uint8Array(SHA256_DIGEST_BYTES);
    const digestView = new DataView(digest.buffer);
    for (let index = 0; index < 8; index += 1) {
        digestView.setUint32(index * 4, at(state, index), false);
    }
    return digest;
}
/** HMAC-SHA256 (RFC 2104): 32 bytes, for a key of any length. */
export function hmacSha256(key, message) {
    const block = new Uint8Array(SHA256_BLOCK_BYTES);
    block.set(key.length > SHA256_BLOCK_BYTES ? sha256(key) : key);
    const inner = new Uint8Array(SHA256_BLOCK_BYTES + message.length);
    const outer = new Uint8Array(SHA256_BLOCK_BYTES + SHA256_DIGEST_BYTES);
    for (let index = 0; index < SHA256_BLOCK_BYTES; index += 1) {
        const byte = block[index] ?? 0;
        inner[index] = byte ^ 0x36;
        outer[index] = byte ^ 0x5c;
    }
    inner.set(message, SHA256_BLOCK_BYTES);
    outer.set(sha256(inner), SHA256_BLOCK_BYTES);
    return sha256(outer);
}
function rotr(value, bits) {
    return ((value >>> bits) | (value << (32 - bits))) >>> 0;
}
/** `noUncheckedIndexedAccess` makes every typed-array read `number | undefined`; this is the read. */
function at(words, index) {
    return words[index] ?? 0;
}
//# sourceMappingURL=sha256.js.map